// v3.4.601 — الحجز من عرض سعر: في نموذج الحجز تُعرض عروض السعر (المعتمدة والمنتظرة) لوحدة الحجز ليُختار أحدها بدل إدخال سعر البيع يدويًا.
// العرض المختار يحدد سعر البيع (والسعر الرسمي والخصم وبرنامج الدفعات)؛ العربون يبقى إدخالًا في الحجز.
// الإدخال اليدوي يبقى متاحًا عند عدم وجود عرض معتمد للوحدة أو بإلغاء اختيار العرض.
import React, { useState, useEffect, useRef } from "react";
import { reservations as resApi } from "../services/api";

const money = (n) => Number(n || 0).toLocaleString("en-SA", { maximumFractionDigits: 2 });
const ST = { approved: ["معتمد", "#dcfce7", "#166534"], pending: ["بانتظار الاعتماد", "#fef3c7", "#92400e"], draft: ["مسودة", "#f1f5f9", "#475569"], rejected: ["مرفوض", "#fee2e2", "#991b1b"] };
const chip = (text, bg, color) => <span style={{ background: bg, color, borderRadius: 10, padding: "1px 8px", fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap" }}>{text}</span>;

function QuoteDetails({ q }) {
  const cell = (label, value, strong) => (
    <div style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 10px" }}>
      <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: strong ? 14 : 12.5, fontWeight: strong ? 800 : 700, color: "#713f12" }}>{value}</div>
    </div>
  );
  const fees = (q.admin_fee || 0) + (q.program_fee || 0);
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
        {cell("السعر الرسمي", money(q.list_price) + " ر.س")}
        {fees !== 0 && cell(fees < 0 ? "خصم السداد النقدي" : (q.admin_fee_label && !q.program_fee ? q.admin_fee_label : "الرسوم"), (fees < 0 ? "− " : "") + money(Math.abs(fees)) + " ر.س")}
        {q.discount > 0 && cell("الخصم", "− " + money(q.discount) + " ر.س")}
        {cell("السعر النهائي", money(q.total_price) + " ر.س", true)}
        {cell("نوع العقد", q.contract_label || "—")}
        {cell("برنامج الدفعات", q.plan_label || "—")}
        {q.valid_until && cell(q.expired ? "انتهت صلاحيته" : "ساري حتى", <span dir="ltr">{q.valid_until}</span>)}
      </div>
      {q.installments?.length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ cursor: "pointer", fontSize: 11.5, fontWeight: 700, color: "#854d0e" }}>جدول الدفعات ({q.installments.length})</summary>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 11.5, background: "#fff" }}>
            <thead><tr style={{ background: "#fef9c3", color: "#713f12" }}>
              {["الدفعة", "النسبة", "المبلغ", "الاستحقاق"].map((h) => <th key={h} style={{ padding: "5px 8px", textAlign: "right", border: "1px solid #fde68a" }}>{h}</th>)}
            </tr></thead>
            <tbody>{q.installments.map((x) => (
              <tr key={x.no}>
                <td style={{ padding: "4px 8px", border: "1px solid #fde68a" }}>{x.label}</td>
                <td style={{ padding: "4px 8px", border: "1px solid #fde68a" }}>{x.pct != null ? x.pct + "%" : "—"}</td>
                <td style={{ padding: "4px 8px", border: "1px solid #fde68a", fontWeight: 700 }}>{money(x.amount)}</td>
                <td style={{ padding: "4px 8px", border: "1px solid #fde68a" }}>{x.due_date || "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </details>
      )}
    </div>
  );
}

const quoteTitle = (q) => "عرض السعر " + (q.quote_no || "#" + q.id);

/** بطاقة للعرض فقط — العرض الذي بُني عليه الحجز */
export function ReservationQuoteCard({ quote }) {
  if (!quote) return null;
  return (
    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: 12, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, fontWeight: 800, color: "#854d0e" }}>
        🧾 السعر مبني على {quoteTitle(quote)}
        {quote.deleted ? chip("محذوف", "#fee2e2", "#991b1b") : chip(...(ST[quote.status] || ST.draft))}
        {quote.client_name && <span style={{ fontWeight: 600, color: "#92400e" }}>— {quote.client_name}</span>}
      </div>
      <QuoteDetails q={quote} />
    </div>
  );
}

/**
 * unit: { unitId, projectId, unitCode } — وحدة الحجز المختارة (أيٌّ منها يكفي) · reservationId للحجز القائم
 * value: quotation_id الحالي · linked: العرض المرتبط المحفوظ (من الخادم) · clientIds: عملاء الحجز [{id,name}]
 * onPick(q) / onClear() — hasUnitPicker: المطوّر مرتبط بمشروع (وإلا لا عروض للوحدات)
 */
export default function ReservationQuotePicker({ unit, reservationId, value, linked, clientIds, onPick, onClear, hasUnitPicker, canOpenQuotations, autoPick, onCandidates }) { // v3.4.608 autoPick/onCandidates
  const [list, setList] = useState(null); // null = لم يُحمَّل
  const [loading, setLoading] = useState(false);
  const { unitId, projectId, unitCode } = unit || {};
  const unitKnown = !!(unitId || (projectId && unitCode));

  useEffect(() => {
    if (!unitKnown) { setList(null); return; }
    let alive = true; setLoading(true);
    const params = { reservation_id: reservationId || undefined };
    if (unitId) params.unit_id = unitId; else { params.project_id = projectId; params.unit_code = unitCode; }
    resApi.unitQuotations(params)
      .then((r) => { if (alive) setList(r.data.quotations || []); })
      .catch(() => { if (alive) setList(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [unitKnown, unitId, projectId, unitCode, reservationId]);

  // تغيّرت الوحدة والعرض المختار ليس من عروضها ⇒ يُلغى الاختيار (يبقى السعر ويعود قابلاً للتعديل)
  const clearRef = useRef(onClear); clearRef.current = onClear;
  useEffect(() => {
    if (value && list && !list.some((q) => q.id === value) && clearRef.current) clearRef.current(true);
  }, [list, value]);

  // v3.4.608 — حجز جديد على وحدة عليها عرض ساري: السعر من العرض — يُختار تلقائيًا عرض عميل الحجز إن وُجد واحد، وإلا العرض الوحيد
  const pickRef = useRef(onPick); pickRef.current = onPick;
  const candRef = useRef(onCandidates); candRef.current = onCandidates;
  const clientKey = (clientIds || []).map((c) => String(c.id)).sort().join(",");
  useEffect(() => {
    const cand = (list || []).filter((q) => q.status === "approved" && !q.expired && !q.taken_by);
    if (candRef.current) candRef.current(cand.length);
    if (!autoPick || value || !cand.length) return;
    const ids = clientKey ? clientKey.split(",") : [];
    const mine = cand.filter((q) => q.client_id && ids.includes(String(q.client_id)));
    const pick = mine.length === 1 ? mine[0] : cand.length === 1 ? cand[0] : null;
    if (pick && pickRef.current) pickRef.current(pick);
  }, [autoPick, list, value, clientKey]);

  const selected = (list || []).find((q) => q.id === value) || (linked && linked.id === value ? linked : null);

  if (!unitKnown) {
    if (selected) return <ReservationQuoteCard quote={selected} />;
    return hasUnitPicker ? <div style={{ fontSize: 11.5, color: "#92400e", marginBottom: 10 }}>🧾 اختر وحدة الحجز لعرض عروض السعر المعتمدة لها.</div> : null;
  }
  if (loading && !list) return <div style={{ fontSize: 11.5, color: "#92400e", marginBottom: 10 }}>⏳ جارٍ جلب عروض السعر للوحدة…</div>;
  if (!list) return selected ? <ReservationQuoteCard quote={selected} /> : null;
  if (!list.length) return (
    <div style={{ fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px dashed #fcd34d", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
      🧾 لا يوجد عرض سعر معتمد لهذه الوحدة — أدخل سعر البيع يدويًا، أو أنشئ عرض سعر للوحدة واعتمده ثم اختره هنا.
      {canOpenQuotations && <> <a href="/quotations" target="_blank" rel="noreferrer" style={{ color: "#b45309", fontWeight: 700 }}>فتح عروض الأسعار ↗</a></>}
    </div>
  );

  const clientSet = new Set((clientIds || []).map((c) => String(c.id)));
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#854d0e", marginBottom: 6 }}>🧾 عروض السعر للوحدة — اختر العرض الذي يُبنى عليه الحجز</div>
      <div style={{ display: "grid", gap: 6 }}>
        {list.map((q) => {
          const isSel = q.id === value;
          const blocked = q.status !== "approved" ? "لا يُختار قبل اعتماده" : q.taken_by ? "مبني عليه الحجز #" + q.taken_by
            : q.expired ? "انتهت صلاحيته — يُمدَّد من قائمة العروض قبل بناء حجز عليه" : null; // v3.4.607
          const sameClient = q.client_id && clientSet.has(String(q.client_id));
          return (
            <button key={q.id} type="button" disabled={!!blocked && !isSel} onClick={() => (isSel ? onClear && onClear(false) : onPick && onPick(q))}
              title={blocked || (isSel ? "إلغاء اختيار العرض" : "اختيار هذا العرض")}
              style={{ textAlign: "right", fontFamily: "inherit", cursor: blocked && !isSel ? "not-allowed" : "pointer", opacity: blocked && !isSel ? 0.6 : 1,
                background: isSel ? "#fef3c7" : "#fff", border: isSel ? "2px solid #d97706" : "1px solid #fde68a", borderRadius: 8, padding: "8px 12px",
                display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 15 }}>{isSel ? "✅" : "⚪"}</span>
              <span style={{ fontWeight: 800, color: "#713f12", fontSize: 12.5 }}>{q.quote_no || "#" + q.id}</span>
              {chip(...(ST[q.status] || ST.draft))}
              {q.expired ? chip("منتهي " + (q.valid_until || ""), "#fee2e2", "#991b1b") : q.status === "approved" && q.valid_until ? chip("ساري حتى " + q.valid_until, "#dcfce7", "#166534") : null}
              <span style={{ fontSize: 12, color: "#57534e" }}>{q.client_name || "—"}{sameClient ? " ✓" : ""}</span>
              <span style={{ fontSize: 11.5, color: "#78716c" }}>{[q.contract_label, q.plan_label].filter(Boolean).join(" · ")}</span>
              <span style={{ marginInlineStart: "auto", fontWeight: 800, color: "#854d0e", fontSize: 13 }}>{money(q.total_price)} ر.س</span>
              {blocked && <span style={{ flexBasis: "100%", fontSize: 10.5, color: "#b45309" }}>{blocked}</span>}
            </button>
          );
        })}
      </div>
      {selected && (
        <>
          {clientSet.size > 0 && selected.client_id && !clientSet.has(String(selected.client_id)) && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 10px" }}>
              ⚠️ العرض المختار باسم «{selected.client_name || "عميل آخر"}» وهو ليس من عملاء هذا الحجز — تأكد من العميل قبل الحفظ.
            </div>
          )}
          {selected.expired && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: "#991b1b", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 10px" }}>
              ⚠️ انتهت صلاحية هذا العرض في <span dir="ltr">{selected.valid_until}</span> — الحجز القائم عليه يبقى، ولا يُبنى عليه حجز جديد إلا بعد تمديده.
            </div>
          )}
          <QuoteDetails q={selected} />
        </>
      )}
      {!selected && list.some((q) => q.status === "approved" && !q.taken_by && !q.expired) && (
        <div style={{ marginTop: 6, fontSize: 11, color: "#92400e" }}>لم يُختر عرض — يمكن إدخال سعر البيع يدويًا بالأسفل، والأفضل اختيار العرض المقدَّم للعميل.</div>
      )}
    </div>
  );
}
